import type { TFunction } from 'i18next';
import type { ApprovalAttributes } from '@/types/approval';
import {
  ACCOUNTING_BP_TYPE,
  normalizeBpState,
  toBpData,
  type BpFormState,
} from '@/utils/approval/businessPartnerRegistration';

/** 회계거래처 신규/변경(101) — 레거시 appr_edit_forms_eas_bp. SAP `Ep`(묶음번호) 진입 전용 */
export { ACCOUNTING_BP_TYPE };

export const isAccountingBpType = (apvType: number): boolean =>
  apvType === ACCOUNTING_BP_TYPE;

/** SAP 신청유형 원문 — 이 값일 때만 업체소싱정보 입력 */
const REGISTER_REQUEST_TYPE = '생성';
const ACCOUNT_CHANGED_FLAG = '001';

export const ACCOUNTING_BP_ACCOUNT_NOTICE =
  '예금주명이 수정되었습니다. 입력한 계좌정보와 계좌사본의 일치 여부를 재확인해주시기 바랍니다.';
/** 번호 없이 진입·조회 실패 — 다른 SAP 진입 양식과 같은 안내(레거시는 알림 없이 빈 표) */
export const ACCOUNTING_BP_MISSING_MESSAGE = '데이터를 불러오지 못했습니다.';
/** SAP 에서 이미 처리된 건 — SAP 문구 다음에 띄움(레거시 동일) */
export const ACCOUNTING_BP_LOAD_ERROR_MESSAGE =
  '문서 종류오류 입니다. 다시 시도해주세요. 이 메시지가 계속 보이면 IT Service 시스템 문제 보고로 등록해주세요.';
export const ACCOUNTING_BP_EMPTY_MESSAGE =
  'SA 회계 거래처 정보 로드에 실패하였습니다.';
export const ACCOUNTING_BP_LOADING_MESSAGE =
  '데이터를 불러오는 중입니다. 잠시 후 다시 시도해주세요.';

export type AccountingBpPartner = {
  seq: string;
  requestType: string;
  taxNumber: string;
  name: string;
  representative: string;
  sapLink: string;
  messageFlag: string;
};

type RawPartner = { [K in keyof AccountingBpPartner]?: string | null };

export type AccountingBpDraftSource = {
  code: number;
  attributes?: { sapDocumentNumber?: string };
  display?: {
    companyName?: string | null;
    subject?: string | null;
    partners?: RawPartner[] | null;
    /** 비어 있지 않으면 SAP 에서 이미 처리된 건 */
    messages?: string[] | null;
  };
};

export type AccountingBpState = {
  sapDocumentNumber: string | null;
  companyName: string;
  partners: AccountingBpPartner[];
  loading: boolean;
  /** 조회로 채운 제목 — 이탈 경고 판정에서 사용자 입력으로 보지 않음 */
  autoSubject?: string;
};

export const createAccountingBpState = (
  sapDocumentNumber: string | null = null,
): AccountingBpState => ({
  sapDocumentNumber,
  companyName: '',
  partners: [],
  loading: false,
});

const text = (v: unknown) => (typeof v === 'string' ? v.trim() : '');

export const toAccountingBpPartners = (
  raw: RawPartner[] | null | undefined,
): AccountingBpPartner[] =>
  (raw ?? []).map((p) => ({
    seq: text(p.seq),
    requestType: text(p.requestType),
    taxNumber: text(p.taxNumber),
    name: text(p.name),
    representative: text(p.representative),
    sapLink: text(p.sapLink),
    messageFlag: text(p.messageFlag),
  }));

/** 레거시는 행을 돌며 덮어써 마지막 행의 신청유형으로 제목·업체소싱정보 노출을 정함 */
const lastRequestType = (partners: AccountingBpPartner[]): string =>
  partners[partners.length - 1]?.requestType ?? '';

export const isAccountingBpRegister = (
  partners: AccountingBpPartner[],
): boolean => lastRequestType(partners) === REGISTER_REQUEST_TYPE;

export const hasAccountChangedNotice = (
  partners: AccountingBpPartner[],
): boolean => partners.some((p) => p.messageFlag === ACCOUNT_CHANGED_FLAG);

/** 행이 없으면 제목을 건드리지 않음(레거시 동일) */
export const buildAccountingBpSubject = (
  partners: AccountingBpPartner[],
  t: TFunction,
): string | null =>
  partners.length
    ? t('비즈니스 파트너 {{requestType}}의 건', {
        requestType: lastRequestType(partners),
        interpolation: { escapeValue: false },
      })
    : null;

export const parseAccountingBpSapDocumentNumber = (
  attributes: Record<string, unknown> | null | undefined,
): string | null => {
  const value = attributes?.sapDocumentNumber;
  return typeof value === 'string' && value.trim() ? value.trim() : null;
};

export const parseAccountingBpContent = (
  attributes: Record<string, unknown> | null | undefined,
): string | null => {
  const content = attributes?.content;
  return typeof content === 'string' && content.trim() ? content : null;
};

/** 레거시 save_doc_eas_bp 의 거래처 표 검사. 업체소싱정보 검사는 checkBpBeforeSave */
export const checkAccountingBpBeforeSave = (
  state: AccountingBpState | null | undefined,
): string | null => {
  if (state?.loading) return ACCOUNTING_BP_LOADING_MESSAGE;
  if (!state?.sapDocumentNumber || !state.partners.length) {
    return ACCOUNTING_BP_EMPTY_MESSAGE;
  }
  return null;
};

/** 업체소싱정보 상태의 신청유형을 SAP 마지막 행에 맞춤 */
export const withAccountingBpRequestType = (
  form: BpFormState,
  partners: AccountingBpPartner[],
): BpFormState => ({
  ...form,
  requestType: isAccountingBpRegister(partners) ? 'register' : 'update',
});

/** companyData 는 레거시 CL02 — '생성'이 아니면 빈 객체 */
export const buildAccountingBpAttributes = (
  state: AccountingBpState | null | undefined,
  form: BpFormState | null | undefined,
  content: string,
  t: TFunction,
): ApprovalAttributes | null => {
  if (!state?.sapDocumentNumber) return null;
  const companyData =
    form && isAccountingBpRegister(state.partners)
      ? toBpData(
          normalizeBpState(withAccountingBpRequestType(form, state.partners)),
          ACCOUNTING_BP_TYPE,
          t,
        )
      : {};
  return { sapDocumentNumber: state.sapDocumentNumber, content, companyData };
};

const LEGACY_FORM_ACTION = /'action'\s*:\s*'([^']+)'/;
const LEGACY_FORM_FIELD =
  /\{\s*'name'\s*:\s*'([^']+)'\s*,\s*'value'\s*:\s*'([^']*)'\s*\}/g;

/** 레거시 저장 HTML 의 「보기」 onclick(POST 폼 스크립트)에서 SAP 링크를 되살림 */
export const parseLegacySapLink = (
  onclick: string | null | undefined,
): string | null => {
  const action = onclick?.match(LEGACY_FORM_ACTION)?.[1];
  if (!onclick || !action) return null;
  let url: URL;
  try {
    url = new URL(action);
  } catch {
    return null;
  }
  for (const [, name, value] of Array.from(
    onclick.matchAll(LEGACY_FORM_FIELD),
  )) {
    url.searchParams.set(name, value);
  }
  return url.toString();
};
